import Section from '../components-library/checkout/sections/Section';
import useBasket from '../hooks/basket/useBasket';
import classnames from 'classnames';
import OrderBasketItem from '../components-library/basket/order-basket-item';
import useScreenType from '../hooks/utility/useScreenType';
import PromoCodeInput from '../components-library/checkout/PromoCodeInput';
import Tips from '../components-library/checkout/Tips';
import useRouter from '../hooks/navigation/useRouter';
import BackIcon from '../assets/logo/BackIcon';
import useTheme from '../hooks/ui/useTheme';
import CheckoutInfoBag, {
  CheckoutInfoBagHandle,
  SectionType,
} from '../components-library/checkout/sections/CheckoutInfoBag';
import useOrderFlow from '../hooks/order/useOrderFlow';
import PayButton from '../components-library/payment/buttons/PayButton';
import EmptyBasketIllustration from '../assets/illustrations/EmptyBasketIllustration';
import PaymentCoordinator from '../components-library/payment/PaymentCoordinator';
import LoadingPage from '../components-library/loading/LoadingPage';
import { useTranslation } from 'react-i18next';
import OrderPaymentBreakdown from '../components-library/order/OrderPaymentBreakdown';
import OrderBasketSummary from '../components-library/order/OrderBasketSummary';
import { CompleteBasketItem } from '../providers/BasketProvider';
import DeliveryThreshold from '../components-library/delivery/DeliveryThreshold';
import { PaymentMethod, SCREENS } from '../services/exports/Constants';
import CheckoutThreshold from '../components-library/checkout/CheckoutThreshold';
import React, { PropsWithChildren, ReactElement, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import useAppMethods from '../hooks/utility/useAppMethods';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import FixedPageLayout from '../components-library/layouts/FixedPageLayout';
import PageHeader from '../components-library/headers/PageHeader';
import CheckoutButton from '../components-library/buttons/CheckoutButton';
import usePaymentFlow from '../hooks/payment/usePaymentFlow';
import useInitialData from '../hooks/global/useInitialData';

const WithWrapper = ({ children }: PropsWithChildren) => {
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return <>{children}</>;
  }

  return <PaymentCoordinator>{children}</PaymentCoordinator>;
};

interface CheckoutScreenProps {
  onBasketItemUpdateClick?: (item: CompleteBasketItem) => void;
  className?: string;
}

interface ICheckoutScreen {
  items: CompleteBasketItem[];
  checkoutInfoBagRef: React.RefObject<CheckoutInfoBagHandle>;
  toggleCheckoutSection: (section: SectionType, open: boolean) => void;
  onBasketItemUpdate: (item: CompleteBasketItem) => void;
  selectedPaymentMethod: PaymentMethod;
  className?: string;
}

const CheckoutScreen = forwardRef<CheckoutInfoBagHandle, CheckoutScreenProps>((props, ref) => {
  const { onBasketItemUpdateClick, className } = props;
  const { items } = useBasket();
  const { data } = useOrderFlow();
  const { data: initialData } = useInitialData();
  const { franchise } = initialData;
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();
  const { toggleMenuItemModal } = useAppMethods();
  const { selectedPaymentMethod } = usePaymentFlow();
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const { toggleOrderMethodModal } = useAppMethods();

  const checkoutInfoBagRef = useRef<CheckoutInfoBagHandle>(null);
  const toggleCheckoutSection = (section: SectionType, open: boolean) =>
    checkoutInfoBagRef.current.toggleSection(section, open);

  const onBasketItemUpdate = (item: CompleteBasketItem): void => {
    if (onBasketItemUpdateClick) {
      return onBasketItemUpdateClick(item);
    }

    toggleMenuItemModal({
      openModal: true,
      product: item.entry,
      isFreeItem: item.is_free,
      lineItem: item,
    });
  };

  useEffect(() => {
    data &&
      !isOrderMethodInfoComplete &&
      !franchise?.has_multiple_companies &&
      !isMobileApp &&
      setTimeout(() => toggleOrderMethodModal({ openModal: true }), 1000);
  }, [data]);

  useImperativeHandle(ref, () => ({ toggleSection: toggleCheckoutSection }));

  const config = {
    items,
    checkoutInfoBagRef,
    toggleCheckoutSection,
    onBasketItemUpdate,
    selectedPaymentMethod,
    className,
  };

  return (
    <WithWrapper>
      {!data.id && <LoadingPage />}
      {isDesktop ? <Desktop {...config} /> : <Mobile {...config} />}
    </WithWrapper>
  );
});

export default CheckoutScreen;

const Desktop = ({
  checkoutInfoBagRef,
  selectedPaymentMethod,
  toggleCheckoutSection,
  onBasketItemUpdate,
}: ICheckoutScreen) => {
  const { items } = useBasket();
  const { data, loading } = useOrderFlow();
  const { navigate } = useRouter();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:CheckoutScreen' });
  const theme = useTheme();

  return (
    <FixedPageLayout
      hasHeader={false}
      leftColumn={
        <div className="py-lg pb-[30px]">
          <button
            onClick={() =>
              navigate({
                web: {
                  to: SCREENS.HOME_SCREEN,
                },
              })
            }
            className="flex items-center w-full p-md text-sm text-brand-text-secondary lg:p-none lg:w-fit"
          >
            <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.gray['700']} />
            {t('buttons.return')}
          </button>

          {Boolean(data.id) && <CheckoutInfoBag ref={checkoutInfoBagRef} />}
          {items.length > 0 && <PayButton showTerms toggleCheckoutSection={toggleCheckoutSection} className="mt-lg" />}
        </div>
      }
      rightColumn={
        <div className="h-full relative">
          <div
            className={
              'h-full p-md rounded-lg flex flex-col space-y-md pb-[200px] relative overflow-y-scroll overscroll-contain scrollbar-hide'
            }
          >
            <div className="bg-brand-card lg:rounded-xl lg:p-md">
              {items.length > 0 ? (
                <>
                  <Section
                    show
                    title={t('sections.order_summary')}
                    className={'!p-0 space-y-md'}
                    titleClassName="!text-brand-text-primary !text-[16px]"
                    hideDivider
                  >
                    <PromoCodeInput titleClassName="!text-brand-text-secondary" />
                    {selectedPaymentMethod !== PaymentMethod.Cash && (
                      <div>
                        <h6 className="text-sm font-medium text-brand-text-secondary">{t('tip')}</h6>
                        <Tips className="mt-sm" />
                      </div>
                    )}

                    <OrderPaymentBreakdown order={data} loading={loading} className="mt-md" />
                  </Section>
                </>
              ) : (
                <EmptyBasket />
              )}
            </div>
            <div className="">
              {items.map((item: CompleteBasketItem, index) => (
                <div className={classnames({ 'mb-md': index !== items.length - 1 })} key={`basket-item-${index}`}>
                  <OrderBasketItem
                    coupon_type={data.coupon_type}
                    data={item}
                    index={index}
                    editable
                    onUpdateClick={() => onBasketItemUpdate(item)}
                  />
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="w-full">
                <CheckoutThreshold className="mt-md" />
                <DeliveryThreshold className="mt-md" />
              </div>
            )}
          </div>
          {items.length > 0 && (
            <CheckoutButton className="bg-brand-card rounded-b-lg absolute w-full px-md bottom-0 border-t-sm">
              <PayButton toggleCheckoutSection={toggleCheckoutSection} className="mt-md" />
            </CheckoutButton>
          )}
        </div>
      }
    />
  );
};

const Mobile = ({ checkoutInfoBagRef, selectedPaymentMethod, className, toggleCheckoutSection }: ICheckoutScreen) => {
  const { items } = useBasket();
  const { data, loading } = useOrderFlow();
  const { navigate } = useRouter();
  const { isMobileApp } = useMobileApp();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:CheckoutScreen' });

  return (
    <>
      <FixedPageLayout
        header={
          !isMobileApp && (
            <PageHeader.Back
              onClick={() =>
                navigate({
                  web: {
                    to: SCREENS.BASKET,
                  },
                })
              }
              title={t('buttons.return')}
            />
          )
        }
        footer={
          items.length > 0 &&
          !isMobileApp && (
            <>
              <CheckoutButton className={classnames('bg-brand-card border-t-sm !pt-none')} hideLoginPrompt hideCashback>
                <PayButton
                  showTerms
                  toggleCheckoutSection={toggleCheckoutSection}
                  className="mt-md !rounded-lg h-10 text-base font-semibold"
                />
              </CheckoutButton>
            </>
          )
        }
      >
        <div className={classnames('grid grid-cols-12 pb-[130px]', className)}>
          <div className="col-span-12 lg:col-span-8 xl:col-span-9 lg:py-lg lg:px-2xl">
            <OrderBasketSummary order={data} items={items} />
            {Boolean(data.id) && <CheckoutInfoBag ref={checkoutInfoBagRef} />}
          </div>
          <div className={'col-span-12 lg:col-span-4 xl:col-span-3 lg:p-lg lg:h-screen lg:sticky lg:top-0'}>
            <div>
              {items.length > 0 ? (
                <>
                  <Section
                    show
                    title={t('sections.order_summary')}
                    className={'border-t !p-md'}
                    titleClassName="!text-brand-text-primary font-semibold !text-[16px]"
                    hideDivider
                  >
                    <div>
                      <PromoCodeInput
                        className={classnames({ 'border-xs border-brand-border rounded-lg p-sm': !isMobileApp })}
                        titleClassName="!text-brand-text-secondary"
                      />
                      {selectedPaymentMethod !== PaymentMethod.Cash && (
                        <div className="mt-md">
                          <h6 className="text-sm font-medium text-brand-text-secondary">{t('tip')}</h6>
                          <Tips className="mt-sm" />
                        </div>
                      )}
                      <OrderPaymentBreakdown order={data} loading={loading} className="mt-md" />
                      <CheckoutThreshold className="mt-md" />
                      <DeliveryThreshold className="mt-md" />
                    </div>
                  </Section>
                </>
              ) : (
                <EmptyBasket />
              )}
            </div>
          </div>
        </div>
      </FixedPageLayout>
    </>
  );
};

const EmptyBasket = (): ReactElement => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:CheckoutScreen' });

  return (
    <div className="grow w-full flex flex-col justify-center items-center z-1">
      <EmptyBasketIllustration />
      <p className="text-center text-sm text-brand-text-secondary">{t('labels.add_items')}</p>
    </div>
  );
};
